import Image from "@/components/ui/image";
import { MoveableItem } from "@/components/ui/moveable-item";
import { MoveableText } from "@/components/ui/moveable-text";
import { MoveableUploadedItem } from "@/components/ui/moveable-upload";
import useUserStore from "@/state/user/useUserStore";
import React, { useCallback, useEffect, useState, useRef } from "react";
import NextImage from "next/image";
import { Watermark } from "@/components/ui/watermark";

const MemeCanvas = ({
  mode,
  divRef,
  canvasRef,
  color,
  hideWatermark,
  enableAutoSave = false,
  template,
}: {
  mode: "asset" | "draw" | "text" | "upload" | "layers";
  divRef: React.RefObject<HTMLDivElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  color: string;
  hideWatermark?: boolean;
  enableAutoSave?: boolean;
  template?: string; // URL to a PNG template with transparent background
}) => {
  const [
    selectedImage,
    uploadedImage,
    assets,
    setAssets,
    isDrawing,
    setIsDrawing,
    strokeColor,
    strokeWidth,
    textFields,
    flipImage,
    hideControls,
    setHideControls,
    history,
    setHistory,
    step,
    setStep,
    ratio,
    uploadAssets,
    setUploadAssets,
    imageFormat,
    local,
    canvasIsLoading,
    setCanvasIsLoading,
    templateIsLoading,
    setTemplateIsLoading,
    isErasing,
  ] = useUserStore((state) => [
    state.selectedImage,
    state.uploadedImage,
    state.assets,
    state.setAssets,
    state.isDrawing,
    state.setIsDrawing,
    state.strokeColor,
    state.strokeWidth,
    state.textFields,
    state.flipImage,
    state.hideControls,
    state.setHideControls,
    state.history,
    state.setHistory,
    state.step,
    state.setStep,
    state.ratio,
    state.uploadAssets,
    state.setUploadAssets,
    state.imageFormat,
    state.local,
    state.canvasIsLoading,
    state.setCanvasIsLoading,
    state.templateIsLoading,
    state.setTemplateIsLoading,
    state.isErasing,
  ]);

  const [border, setBorder] = useState<string | null>(null);

  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const points: { x: number; y: number }[] = [];

  useEffect(() => {
    setCanvasIsLoading(true);
    setTemplateIsLoading(true);
  }, []);

  const [watermark, setWatermark] = useState(hideWatermark !== true);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const rect = canvas.getBoundingClientRect();

      // Set the canvas size to match the display size
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      // Scale all drawing operations by the device pixel ratio
      if (ctx) {
        ctx.scale(dpr, dpr);

        // Set the CSS size
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;

        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
      }
    }
  }, []);

  const getPointFromEvent = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return null;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    // Get the CSS width/height (without transforms)
    const displayWidth = rect.width;
    const displayHeight = rect.height;

    // Get the actual canvas dimensions
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Calculate scaling factors
    const scaleX = canvasWidth / displayWidth;
    const scaleY = canvasHeight / displayHeight;

    // Get the cursor position relative to the canvas element
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    return { x, y };
  };

  const drawSmoothLine = (
    start: { x: number; y: number },
    end: { x: number; y: number },
    context: CanvasRenderingContext2D
  ) => {
    const dpr = window.devicePixelRatio || 1;

    context.beginPath();
    context.moveTo(start.x / dpr, start.y / dpr);

    const controlPoint = {
      x: (start.x + end.x) / (2 * dpr),
      y: (start.y + end.y) / (2 * dpr),
    };

    context.quadraticCurveTo(
      controlPoint.x,
      controlPoint.y,
      end.x / dpr,
      end.y / dpr
    );

    if (isErasing) {
      context.globalCompositeOperation = "destination-out";
      context.strokeStyle = "rgba(0,0,0,1)";
    } else {
      context.globalCompositeOperation = "source-over";
      context.strokeStyle = strokeColor;
    }

    context.stroke();
    context.globalCompositeOperation = "source-over";
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || mode !== "draw") return;
    const point = getPointFromEvent(event);
    if (!point) return;

    const context = canvasRef.current.getContext("2d");
    if (!context) return;

    setIsDrawing(true);
    lastPoint.current = point;
    points.length = 0;
    points.push(point);

    context.lineWidth = strokeWidth;
    context.lineCap = "round";
    context.lineJoin = "round";
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (
      !isDrawing ||
      !canvasRef.current ||
      mode !== "draw" ||
      !lastPoint.current
    )
      return;
    const newPoint = getPointFromEvent(event);
    if (!newPoint) return;

    const context = canvasRef.current.getContext("2d");
    if (!context) return;

    points.push(newPoint);
    if (points.length > 3) {
      const lastTwoPoints = points.slice(-2);
      const controlPoint = lastTwoPoints[0];
      const endPoint = {
        x: (lastTwoPoints[0].x + lastTwoPoints[1].x) / 2,
        y: (lastTwoPoints[0].y + lastTwoPoints[1].y) / 2,
      };
      drawSmoothLine(lastPoint.current, controlPoint, context);
      drawSmoothLine(controlPoint, endPoint, context);
      lastPoint.current = endPoint;
    }
  };

  const handleMouseUp = () => {
    if (!canvasRef.current || !lastPoint.current) return;
    const context = canvasRef.current.getContext("2d");
    if (!context) return;

    if (points.length > 3) {
      const lastTwoPoints = points.slice(-2);
      const controlPoint = lastTwoPoints[0];
      const endPoint = lastTwoPoints[1];
      drawSmoothLine(lastPoint.current, controlPoint, context);
      drawSmoothLine(controlPoint, endPoint, context);
    }

    saveState();
    setIsDrawing(false);
    lastPoint.current = null;
    points.length = 0;
  };

  const removeAssetHandler = (id: string): void => {
    setAssets(assets.filter((asset) => asset.id !== id));
  };

  const removeUploadAssetHandler = (id: string): void => {
    setUploadAssets(uploadAssets.filter((asset) => asset.id !== id));
  };

  const saveState = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      // Create a temporary canvas to store the state at the correct scale
      const tempCanvas = document.createElement("canvas");
      const tempCtx = tempCanvas.getContext("2d");

      if (tempCtx) {
        // Set the temporary canvas to the same dimensions
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;

        // Copy the current canvas state
        tempCtx.drawImage(canvas, 0, 0);

        // Save the state
        setStep(step + 1);
        const newHistory = history.slice(0, step + 1);
        newHistory.push(tempCanvas.toDataURL());
        setHistory(newHistory);

        // Save to localStorage only if auto-save is enabled
        if (enableAutoSave) {
          localStorage.setItem("drawingHistory", JSON.stringify(newHistory));
          localStorage.setItem("drawingStep", (step + 1).toString());
        }
      }
    }
  }, [canvasRef, step, history, setHistory, setStep, enableAutoSave]);

  // Updated function to create the cursor URL with a crosshair
  const createCursorURL = useCallback(() => {
    const cursorSize = Math.max(strokeWidth * 2, 16);
    const canvas = document.createElement("canvas");
    canvas.width = cursorSize;
    canvas.height = cursorSize;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      if (isErasing) {
        // Draw eraser cursor
        ctx.beginPath();
        ctx.arc(
          cursorSize / 2,
          cursorSize / 2,
          strokeWidth / 2,
          0,
          2 * Math.PI
        );
        ctx.strokeStyle = "#000000";
        ctx.stroke();
        ctx.fillStyle = "#ffffff";
        ctx.fill();
      } else {
        // Draw brush cursor
        ctx.beginPath();
        ctx.arc(
          cursorSize / 2,
          cursorSize / 2,
          strokeWidth / 2,
          0,
          2 * Math.PI
        );
        ctx.fillStyle = strokeColor;
        ctx.fill();
      }

      // Draw the crosshair
      const crosshairSize = Math.min(cursorSize / 3, 12);
      ctx.lineWidth = 1;

      // Horizontal line
      ctx.beginPath();
      ctx.moveTo(cursorSize / 2 - crosshairSize, cursorSize / 2);
      ctx.lineTo(cursorSize / 2 + crosshairSize, cursorSize / 2);
      ctx.strokeStyle = "#000000";
      ctx.stroke();

      // Vertical line
      ctx.beginPath();
      ctx.moveTo(cursorSize / 2, cursorSize / 2 - crosshairSize);
      ctx.lineTo(cursorSize / 2, cursorSize / 2 + crosshairSize);
      ctx.strokeStyle = "#000000";
      ctx.stroke();
    }
    return `url(${canvas.toDataURL()}) ${cursorSize / 2} ${
      cursorSize / 2
    }, auto`;
  }, [strokeColor, strokeWidth, isErasing]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          border: "4px solid #000000",
          boxShadow: "2px 2px 0px 0px #000000",
          overflow: "hidden",
          aspectRatio: ratio,
          maxWidth: "100%",
          width: imageFormat === "banner" ? "100%" : "auto",
          height: "auto",
          maxHeight:
            imageFormat === "banner"
              ? "calc(100vh - 328px)"
              : "calc(100vh - 322px)",
          borderRadius: "0.25rem",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            background: template ? "transparent" : "#fff",
          }}
          ref={divRef}
          onMouseEnter={() => mode !== "draw" && setHideControls(false)}
          onMouseLeave={() => mode !== "draw" && setHideControls(true)}
        >
          {!selectedImage ? (
            <div
              style={{
                width: "100%",
                height: "100%",
                position: "absolute",
                transform: flipImage ? "scaleX(-1)" : "scaleX(1)",
              }}
            ></div>
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                position: "absolute",
                transform: flipImage ? "scaleX(-1)" : "scaleX(1)",
              }}
            >
              <Image
                src={selectedImage}
                style={{
                  objectFit: "cover",
                  objectPosition: "center",
                }}
                alt=""
                fill
                sizes={
                  imageFormat === "banner"
                    ? "100vw"
                    : "(max-width: 1920px) 50vw, 35vw"
                }
                local={local}
                onLoad={() => {
                  setCanvasIsLoading(false);
                }}
              />
            </div>
          )}
          {uploadedImage && (
            <div
              style={{
                width: "100%",
                height: "100%",
                position: "absolute",
                transform: flipImage ? "scaleX(-1)" : "scaleX(1)",
              }}
            >
              <NextImage
                src={uploadedImage}
                alt=""
                style={{
                  objectFit: "cover",
                  objectPosition: "center",
                }}
                layout="fill"
                onLoad={() => setCanvasIsLoading(false)}
              />
            </div>
          )}
          {textFields.map((textField) => (
            <MoveableText
              key={textField.id}
              textField={textField}
              hideControls={hideControls}
              color={color}
              startPosition={
                textField.startPosition ? textField.startPosition : undefined
              }
              width={textField.width ? textField.width : undefined}
            />
          ))}
          {assets &&
            assets.map((asset) => (
              <MoveableItem
                key={asset.id}
                asset={asset}
                hideControls={hideControls}
                remove={() => removeAssetHandler(asset.id)}
                color={color}
              />
            ))}
          {uploadAssets &&
            uploadAssets.map((asset) => (
              <MoveableUploadedItem
                key={asset.id}
                hideControls={hideControls}
                remove={() => removeUploadAssetHandler(asset.id)}
                color={color}
                uploadAsset={asset}
              />
            ))}
          {watermark && (
            <Watermark
              remove={() => setWatermark(false)}
              color={color}
              hideControls={hideControls}
            />
          )}
          {template && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none", // Allow drawing through the template
                zIndex: 1, // Place above the background but below the canvas
              }}
            >
              <Image
                src={template}
                alt="Template"
                fill
                style={{
                  objectFit: "contain",
                  mixBlendMode: "multiply", // This helps with transparency
                }}
                onLoad={() => setTemplateIsLoading(false)}
              />
            </div>
          )}
          <canvas
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: mode === "draw" ? "auto" : "none",
              cursor: mode === "draw" ? createCursorURL() : "auto",
              zIndex: 2, // Place above the template
            }}
            ref={canvasRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          />
        </div>
      </div>
    </div>
  );
};

export default MemeCanvas;
